import { type ScrollDirection } from '@/presentation/hooks/useScrollDirection';

export type MobileSecondaryNavTransitionState = 'hidden' | 'settled' | 'shown';

export interface MobileSecondaryNavState {
  anchor: number;
  isVisible: boolean;
  transitionState: MobileSecondaryNavTransitionState;
}

export interface ResolveMobileSecondaryNavStateInput {
  hasVariant: boolean;
  hideDistance: number;
  isAtTop: boolean;
  navHeight: number;
  scrollDirection: ScrollDirection;
  scrollY: number;
  showDistance: number;
}

export function resolveMobileSecondaryNavState(
  currentState: MobileSecondaryNavState,
  input: ResolveMobileSecondaryNavStateInput
): MobileSecondaryNavState {
  const navHeight = Math.max(0, input.navHeight);

  if (!input.hasVariant) {
    return {
      anchor: input.scrollY,
      isVisible: false,
      transitionState: 'settled',
    };
  }

  if (input.isAtTop) {
    return {
      anchor: input.scrollY,
      isVisible: true,
      transitionState: 'settled',
    };
  }

  if (!input.scrollDirection) {
    return currentState;
  }

  const distanceFromAnchor = input.scrollY - currentState.anchor;

  if (currentState.isVisible) {
    if (
      input.scrollDirection === 'up' &&
      distanceFromAnchor <= -input.showDistance
    ) {
      return {
        anchor: input.scrollY,
        isVisible: true,
        transitionState:
          currentState.transitionState === 'shown' ? 'shown' : 'settled',
      };
    }

    const hideThreshold =
      currentState.transitionState === 'shown'
        ? input.hideDistance + navHeight
        : input.hideDistance;

    if (
      input.scrollDirection === 'down' &&
      distanceFromAnchor >= hideThreshold
    ) {
      return {
        anchor: input.scrollY - navHeight,
        isVisible: false,
        transitionState: 'hidden',
      };
    }

    return currentState;
  }

  const hiddenDownThreshold =
    currentState.transitionState === 'hidden'
      ? input.hideDistance + navHeight
      : input.hideDistance;

  if (
    input.scrollDirection === 'down' &&
    distanceFromAnchor >= hiddenDownThreshold
  ) {
    return {
      anchor: input.scrollY,
      isVisible: false,
      transitionState: 'settled',
    };
  }

  if (
    input.scrollDirection === 'up' &&
    distanceFromAnchor <= -input.showDistance
  ) {
    return {
      anchor: input.scrollY + navHeight,
      isVisible: true,
      transitionState: 'shown',
    };
  }

  return currentState;
}
